import classnames from 'classnames'
import { useField } from 'formik'
import * as React from 'react'
import { DeleteIcon } from 'src/assets/icons/customIcons/upload-icons/Delete'
import { formatFileSize } from 'src/helpers/fns'
import { useTranslatable } from 'src/hooks/locale/utils'
import FileIcon from 'src/views/components/FileIcon'
import { useFormData } from './Form'

interface Props {
  readonly name: string
  readonly label?: string
  /** Lowercase, without the dot. */
  readonly extensions: readonly string[]
  /** In bytes. */
  readonly maxSize: number
  readonly required?: boolean
  readonly disabled?: boolean
}

function extensionOf(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? ''
}

/**
 * One file that travels with the form's own request (multipart), as `File | null`.
 * Unlike `FileInput`, which uploads to media files as soon as a file is chosen.
 */
export default function LocalFileInput({
  name,
  label,
  extensions,
  maxSize,
  required = false,
  disabled = false,
}: Props): React.ReactElement {
  const t = useTranslatable()

  const validate = React.useCallback(
    (value: File | null): string | undefined => {
      if (value == null) {
        if (!required) return undefined
        return label != null ? `${label} ${t('common:is_required')}` : t('common:field_is_required')
      }
      if (!extensions.includes(extensionOf(value.name))) return t('error:file_extension_not_allowed')
      if (value.size > maxSize) return t('error:file_is_larger')
      return undefined
    },
    [extensions, label, maxSize, required, t]
  )

  const [field, meta, helpers] = useField<File | null>({ name, validate })
  const [formData] = useFormData()
  const serverErrors = formData.errors.fields[name] ?? []

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      void helpers.setTouched(true, false)
      void helpers.setValue(e.target.files?.[0] ?? null)
    },
    [helpers]
  )

  const handleRemove = React.useCallback(() => {
    void helpers.setValue(null)
  }, [helpers])

  const file = field.value
  const accept = extensions.map((extension) => `.${extension}`).join(', ')

  return (
    <div className="flex flex-col">
      {label != null && (
        <label className="mb-[10px] flex flex-nowrap items-center font-bold text-primaryTextColor">
          {label}
          {required && <span className="pl-1 text-errorMessages">*</span>}
        </label>
      )}
      {file == null ? (
        <label
          className={classnames(
            'flex h-[48px] w-[200px] cursor-pointer items-center justify-center rounded-[4px] bg-primaryBlue px-[20px] text-white hover:opacity-80 xxs:px-[10px]',
            { 'cursor-not-allowed opacity-40 hover:!opacity-40': disabled }
          )}
        >
          <span>{t('file:choose_file')}</span>
          <input
            className="hidden"
            type="file"
            accept={accept}
            onChange={handleChange}
            disabled={disabled}
            data-testid={name}
          />
        </label>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-primaryTextColor">
          <FileIcon type={extensionOf(file.name)} />
          <span className="break-all">{file.name}</span>
          <span className="whitespace-nowrap text-captionColor">{formatFileSize(file.size)}</span>
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            title={t('common:delete')}
            className="cursor-pointer rounded-full p-[3px] hover:bg-hover-icon disabled:cursor-not-allowed"
            data-testid={`${name}-remove`}
          >
            <DeleteIcon />
          </button>
        </div>
      )}
      {meta.touched && meta.error != null && <div className="mt-2 text-[13px] text-errorMessages">{meta.error}</div>}
      {serverErrors.map((error, i) => (
        <div key={i} className="mt-2 text-[13px] text-errorMessages">
          {error}
        </div>
      ))}
    </div>
  )
}
